import { config } from '../config';
import { log } from '../app/logger';
import { errorMessage } from '../app/errors';
import type { AssemblyVariant, PlayabilityResult } from '../types';

/**
 * Design §8.2's playability probe — *"an `HTMLAudioElement.canplaythrough` on the assembled blob,
 * bounded by a timeout"* — built here first because build unit U-1 is what decides whether the
 * design needs it at all.
 *
 * BUILD-UNIT NOTE: a declared thin slice of U-11's probe. U-11 owns the product behaviour it feeds
 * (labelling a segment "audio unavailable — flagged at 34′"); this is the measurement half.
 *
 * ── Why three signals and not one ────────────────────────────────────────────
 * `canplaythrough` is the strict answer and the only one the A-2 verdict uses. But a fragmented
 * MP4 missing its final fragment can parse far enough to yield `loadedmetadata` and a duration
 * while still never reaching `canplaythrough`, and those two outcomes have different meanings:
 * "the container is broken" versus "the browser would not commit". Reporting only the strict flag
 * would collapse them and hide the more interesting one.
 *
 * The probe is also capable of a **false negative**: iOS is conservative about loading media
 * without user activation, so a timeout here does not prove a file is broken. That is why the
 * harness offers a real Play control per variant and records what Andrew heard
 * (`SegmentRecord.manualVerdict`). The probe is the instrument; the ear is the acceptance test.
 */

const MODULE = 'playability';

/** The DOM surface the probe uses, named so it can be faked in Node. */
export interface AudioElementLike {
  src: string;
  preload: string;
  duration: number;
  readyState: number;
  error: { code: number; message?: string } | null;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  load(): void;
}

export interface ProbeDeps {
  createAudio(): AudioElementLike;
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  now(): number;
}

/**
 * The browser implementation.
 *
 * @throws {Error} when there is no `Audio` constructor — under Node, say. The caller records that
 *   as the probe's `error` field rather than as an unplayable segment: "we could not ask" and "the
 *   answer is no" must not read the same.
 */
export function browserProbeDeps(): ProbeDeps {
  const g = globalThis as unknown as {
    Audio?: new () => AudioElementLike;
    URL?: { createObjectURL(b: Blob): string; revokeObjectURL(u: string): void };
    performance?: { now(): number };
  };
  return {
    createAudio() {
      if (typeof g.Audio !== 'function') {
        throw new Error('No Audio constructor in this environment.');
      }
      return new g.Audio();
    },
    createObjectURL(blob) {
      if (!g.URL) throw new Error('No URL.createObjectURL in this environment.');
      return g.URL.createObjectURL(blob);
    },
    revokeObjectURL(url) {
      g.URL?.revokeObjectURL(url);
    },
    now() {
      return g.performance?.now() ?? Date.now();
    },
  };
}

export interface ProbeInput {
  blob: Blob;
  variant: AssemblyVariant;
  chunkCount: number;
  mime: string;
}

/**
 * Asks the browser to play the assembled blob through, bounded by
 * `PLAYABILITY_PROBE_TIMEOUT_MS` (design §13.1: "3 s, no retry — treat as unplayable").
 *
 * Never throws. A probe that cannot run is a probe result with an `error`, because a spike that
 * aborts on the first unplayable segment cannot report *how many* were unplayable, which is the
 * whole question.
 */
export async function probeBlob(
  input: ProbeInput,
  deps: ProbeDeps = browserProbeDeps(),
  timeoutMs: number = config.PLAYABILITY_PROBE_TIMEOUT_MS,
): Promise<PlayabilityResult> {
  const base: PlayabilityResult = {
    variant: input.variant,
    chunkCount: input.chunkCount,
    sizeBytes: input.blob.size,
    mime: input.mime,
    canPlayThrough: false,
    metadataLoaded: false,
    durationSeconds: null,
    mediaErrorCode: null,
    mediaErrorMessage: null,
    probeMs: 0,
    timedOut: false,
    error: null,
  };

  if (input.blob.size === 0) {
    return {
      ...base,
      error:
        'The assembled blob is empty — there was nothing to probe. This is a missing-chunk problem, not a container problem.',
    };
  }

  let audio: AudioElementLike;
  let url: string;
  try {
    audio = deps.createAudio();
    url = deps.createObjectURL(input.blob);
  } catch (cause) {
    return { ...base, error: `Could not start the probe: ${errorMessage(cause)}` };
  }

  const t0 = deps.now();
  const result: PlayabilityResult = { ...base };

  await new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      audio.removeEventListener('canplaythrough', onCanPlay);
      audio.removeEventListener('loadedmetadata', onMetadata);
      audio.removeEventListener('error', onError);
      resolve();
    };

    function captureDuration(): void {
      const d = audio.duration;
      // A fragmented file with no `moov` commonly reports `Infinity` or `NaN`. Recording the raw
      // value would put a non-finite number in the export and break the stats; recording `null`
      // and the flag separately keeps both facts.
      result.durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
    }

    function onMetadata(): void {
      result.metadataLoaded = true;
      captureDuration();
    }

    function onCanPlay(): void {
      result.canPlayThrough = true;
      captureDuration();
      finish();
    }

    function onError(): void {
      result.mediaErrorCode = audio.error?.code ?? null;
      result.mediaErrorMessage = audio.error?.message ?? null;
      finish();
    }

    audio.addEventListener('canplaythrough', onCanPlay);
    audio.addEventListener('loadedmetadata', onMetadata);
    audio.addEventListener('error', onError);

    timer = setTimeout(() => {
      result.timedOut = true;
      finish();
    }, timeoutMs);

    audio.preload = 'auto';
    audio.src = url;
    try {
      audio.load();
    } catch (cause) {
      result.error = `load() threw: ${errorMessage(cause)}`;
      finish();
    }
  });

  result.probeMs = deps.now() - t0;

  // Detach the source before revoking, so the element cannot hold a URL that no longer resolves.
  try {
    audio.src = '';
    deps.revokeObjectURL(url);
  } catch (cause) {
    log.warn(MODULE, 'object URL cleanup failed', { error: errorMessage(cause) });
  }

  log.info(MODULE, 'probe complete', {
    variant: result.variant,
    bytes: result.sizeBytes,
    canPlayThrough: result.canPlayThrough,
    metadataLoaded: result.metadataLoaded,
    durationSeconds: result.durationSeconds,
    mediaErrorCode: result.mediaErrorCode,
    timedOut: result.timedOut,
  });

  return result;
}
